/**
 * Runtime configuration loaded from /config.json at startup.
 *
 * This decouples environment-specific values from the build,
 * enabling a single frontend bundle for all environments (12-factor).
 *
 * **config.json is PUBLIC.** It is served unauthenticated from CloudFront, so
 * every field here is readable by anyone who can reach the site. Only non-secret
 * values belong in it: base URLs, feature flags, environment labels. A
 * credential never does — if the browser needs data from an authenticated third
 * party, the call goes through our backend, which holds the credential
 * server-side. `infrastructure/scripts/assert-public-config.py` enforces this
 * at deploy time against an allowlist of field names.
 */

interface AppConfig {
  apiBaseUrl: string
  /**
   * Base URL of the Fiscozen backoffice, used to build the operator-facing deep
   * links to `/backoffice/utenti/<id>` (see utils/fzBackoffice.ts), and as the
   * flag for whether the alert integration is wired in this environment. A
   * public URL, not a credential. Empty string disables it.
   *
   * The backoffice *data* calls are NOT made from the browser: they go to our
   * API, which talks to bo_api server-side. See api/services/fzBackoffice.service.ts.
   */
  fzBackofficeBaseUrl: string
}

let _config: AppConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  fzBackofficeBaseUrl: import.meta.env.VITE_FZ_BACKOFFICE_BASE_URL || '',
}

/**
 * Fetch /config.json and store the result.
 * Must be called once before app mount.
 * Falls back to defaults if the fetch fails.
 */
export async function loadConfig(): Promise<void> {
  try {
    const res = await fetch('/config.json')
    if (res.ok) {
      const json = await res.json()
      _config = { ..._config, ...json }
    }
  } catch {
    // Fall back to defaults — local dev without config.json
  }
}

/** Return the current runtime config. Only valid after loadConfig() resolves. */
export function getConfig(): AppConfig {
  return _config
}
